import { colorsToCssBlock } from './themeColorTokens'
import { isCustomThemeId, themes } from './themes'

const CUSTOM_THEMES_STORAGE_KEY = 'portfolio-custom-themes-v1'
export const MAX_CUSTOM_THEMES = 3

const CUSTOM_THEME_STYLE_ID = 'portfolio-custom-themes-style'

export const readCustomThemes = () => {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(CUSTOM_THEMES_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (entry) =>
        entry &&
        typeof entry.id === 'string' &&
        isCustomThemeId(entry.id) &&
        typeof entry.label === 'string' &&
        entry.colors &&
        typeof entry.colors === 'object'
    )
  } catch {
    return []
  }
}

const writeCustomThemes = (entries) => {
  if (typeof window === 'undefined') return

  try {
    if (entries.length) {
      window.localStorage.setItem(CUSTOM_THEMES_STORAGE_KEY, JSON.stringify(entries))
    } else {
      window.localStorage.removeItem(CUSTOM_THEMES_STORAGE_KEY)
    }
  } catch {
    // Ignore quota / private-mode failures
  }
}

const getCustomTheme = (themeId) => readCustomThemes().find((entry) => entry.id === themeId) || null

export const getThemeLabel = (themeId) => {
  const custom = getCustomTheme(themeId)
  if (custom) return custom.label
  return themes[themeId]?.label || themeId
}

export const isSavedThemeId = (themeId) =>
  Boolean(themes[themeId]) || Boolean(getCustomTheme(themeId))

export const injectCustomThemeStyles = () => {
  if (typeof document === 'undefined') return

  const css = readCustomThemes()
    .map((entry) => colorsToCssBlock(entry.id, entry.colors))
    .join('\n\n')

  let styleEl = document.getElementById(CUSTOM_THEME_STYLE_ID)
  if (!css) {
    styleEl?.remove()
    return
  }

  if (!styleEl) {
    styleEl = document.createElement('style')
    styleEl.id = CUSTOM_THEME_STYLE_ID
    document.head.appendChild(styleEl)
  }

  styleEl.textContent = css
}

export const createCustomTheme = ({
  label,
  sourceThemeId,
  colors,
}) => {
  const existing = readCustomThemes()
  if (existing.length >= MAX_CUSTOM_THEMES) {
    return { ok: false, error: 'max' }
  }

  const trimmedLabel = label?.trim() || `Custom ${existing.length + 1}`
  const id = `custom-${Date.now()}`
  const baseThemeId = isCustomThemeId(sourceThemeId)
    ? getCustomTheme(sourceThemeId)?.baseThemeId || 'maya'
    : sourceThemeId

  const entry = {
    id,
    label: trimmedLabel,
    baseThemeId,
    colors: { ...colors },
    createdAt: new Date().toISOString(),
  }

  writeCustomThemes([...existing, entry])
  injectCustomThemeStyles()

  return { ok: true, id, entry }
}

export const updateCustomThemeColors = (themeId, colors) => {
  const custom = readCustomThemes()
  const index = custom.findIndex((entry) => entry.id === themeId)
  if (index === -1) return false

  custom[index] = {
    ...custom[index],
    colors: { ...colors },
  }
  writeCustomThemes(custom)
  injectCustomThemeStyles()
  return true
}

export const deleteCustomTheme = (themeId) => {
  if (!isCustomThemeId(themeId)) return false

  const next = readCustomThemes().filter((entry) => entry.id !== themeId)
  writeCustomThemes(next)
  injectCustomThemeStyles()
  return true
}
